<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  AtomButton,
  WoLocation,
  AtomRadio,
} from "@chatfood/design-system";
import { ref, computed } from "vue";
import { SchedulePicker } from "../schedule-picker";
import { BasketEventName } from "../basket-event-name";
import { BasketPax } from "../basket-pax";
import type { IPickupOrderingDetailsEmits } from "./emits";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import type { IDomainOutletState } from "~/domain/outlet-state";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import { useDomainLabel } from "~/composables/use-domain-label";

const emits = defineEmits<IPickupOrderingDetailsEmits>();

const css = bemBuilder("pickup-ordering-details");
const { t } = useLocale();
const { push, resolvePath } = useNavigation();
const { outletStateMap } = useDomainLabel();

const basket = useBasketStore();
const outlet = useOutletStore();
const business = useBusinessStore();

type IOrderType = "asap" | "scheduled";

const orderingModeSettings = outlet.orderingModes.pickup;
const scheduledTime = ref<IDomainOrderScheduledTime | undefined>(
  basket.orderingMode === "pickup" ? basket.scheduledTime : undefined,
);
const orderType = ref<IOrderType>(
  orderingModeSettings.acceptsAsapOrders &&
    orderingModeSettings.outletState === "open" &&
    !scheduledTime.value
    ? "asap"
    : "scheduled",
);
const isSettingMode = ref(false);
const selectedOutletId = ref<string | undefined>(basket.outletId);

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: "open",
  closed: "close",
  pre_order_only: "soon",
};

const cardDescription = computed(() => {
  if (
    orderingModeSettings.outletState === "closed" ||
    !orderingModeSettings.preparationMinutes
  ) {
    return "";
  }

  return t("components.pickup_ordering_details.outlet_card_hint", {
    prepStart: orderingModeSettings.preparationMinutes,
    prepEnd: orderingModeSettings.preparationMinutes + ETA_PREPARATION_MARGIN,
  });
});

const isAsapSelected = computed(() => orderType.value === "asap");
const isScheduledSelected = computed(() => orderType.value === "scheduled");

const onConfirm = async () => {
  isSettingMode.value = true;

  if (basket.orderingMode !== "pickup") {
    await push("outlet", {
      orderingMode: "pickup",
      outletSlug: outlet.slug,
    });
  }

  if (business.isCatering) {
    await basket.setMeta({
      eventName: eventName.value ?? null,
      pax: typeof pax.value === "number" ? pax.value : null,
    });
  }

  await basket.setScheduledTime(scheduledTime.value);

  emits("update");

  isSettingMode.value = false;
};

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduledTime.value = value;
};

const eventName = ref(basket.eventName);
const pax = ref(basket.pax);
</script>

<template>
  <div :class="css('')">
    <div :class="css('mode-config')">
      <AtomText type="subtitle">
        {{ t("components.pickup_ordering_details.title") }}
      </AtomText>
      <WoLocation
        :class="css('outlet-card')"
        :address="outlet.address.location"
        :state="mapState[orderingModeSettings.outletState]"
        :state-label="outletStateMap[orderingModeSettings.outletState]"
        :name="outlet.name"
        :state-description="cardDescription"
        :is-active="true"
      />
      <NuxtLink
        :to="
          resolvePath('order', {
            orderingMode: 'pickup',
          })
        "
      >
        <AtomText type="link" color="color-primary">
          {{ t("components.pickup_ordering_details.change_outlet_cta") }}
        </AtomText>
      </NuxtLink>
    </div>
    <div v-if="business.isCatering" :class="css('meta-fields')">
      <AtomText type="subtitle">
        {{ t("components.pickup_ordering_details.details") }}
      </AtomText>
      <BasketEventName @update="(value) => (eventName = value)" />
      <BasketPax @update="(value) => (pax = value)" />
    </div>
    <div
      v-if="orderingModeSettings.outletState !== 'closed'"
      :class="css('scheduler')"
    >
      <AtomText type="subtitle">
        {{ t("components.pickup_ordering_details.schedule_title") }}
      </AtomText>
      <AtomRadio
        v-if="
          orderingModeSettings.acceptsAsapOrders &&
          orderingModeSettings.outletState === 'open'
        "
        name="pickupScheduleOrder"
        :checked="isAsapSelected"
        value="asap"
        @change="() => (orderType = 'asap')"
      >
        {{
          t("components.pickup_ordering_details.asap_radio", {
            from: orderingModeSettings.preparationMinutes,
            to:
              orderingModeSettings.preparationMinutes + ETA_PREPARATION_MARGIN,
          })
        }}
      </AtomRadio>
      <AtomRadio
        v-if="orderingModeSettings.acceptsPreOrders"
        name="pickupScheduleOrder"
        :checked="isScheduledSelected"
        value="scheduled"
        @change="orderType = 'scheduled'"
      >
        {{ t("components.pickup_ordering_details.schedule_radio") }}
      </AtomRadio>
      <SchedulePicker
        v-if="
          orderingModeSettings.acceptsPreOrders &&
          isScheduledSelected &&
          selectedOutletId
        "
        :is-root="true"
        ordering-mode="pickup"
        :show-label="false"
        :outlet-id="selectedOutletId"
        picker-align="row"
        :value="scheduledTime"
        @select="onSetSchedule"
        @vue:unmounted="() => onSetSchedule()"
      />
    </div>
    <div :class="css('footer')">
      <AtomButton
        size="large"
        :is-loading="isSettingMode"
        wide
        :is-disabled="orderingModeSettings.outletState === 'closed'"
        @click="onConfirm"
      >
        {{ t("components.pickup_ordering_details.main_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pickup-ordering-details {
  min-width: 375px;

  &__mode-config {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__meta-fields {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  }

  &__outlet-card {
    cursor: default;
  }

  &__scheduler {
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-lg) var(--spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__footer {
    margin: var(--spacing-lg) var(--spacing-md);
  }
}
</style>
